import { resolveThreadStatusKey } from '@/lib/thread-status/store';

export interface RecencyThreadItem {
  id: string;
  remoteId?: string;
  lastMessageAt?: string | number | Date;
}

/**
 * Recency-DESC ordering of a thread list, returned as `threadIds`-relative indices (NOT
 * threadItems-relative — the by-index provider resolves against `threadIds`, and a mismatch
 * crashes rendering with "Key undefined").
 *
 * Each thread sorts by `max(lastMessageAt, lastActivityAt)`: `lastMessageAt` is boot metadata and
 * is never bumped when a new message streams, so `activityAt` (live run start/end, keyed by the
 * durable thread uuid) is what re-floats an OLD thread to the top when the user continues it
 * mid-session. Threads with neither timestamp (brand-new / in-flight) sort first; ties break by
 * the original index (stable).
 */
export function computeRecencyOrder({
  threadIds,
  threadItems,
  activityAt,
}: {
  threadIds: readonly string[];
  threadItems: readonly RecencyThreadItem[];
  activityAt: Record<string, number>;
}): number[] {
  const itemById = new Map(threadItems.map(item => [item.id, item]));
  return threadIds
    .map((id, index) => {
      const item = itemById.get(id);
      const messageAt = item?.lastMessageAt ? new Date(item.lastMessageAt).getTime() : 0;
      const key = item ? resolveThreadStatusKey(item) : undefined;
      const liveAt = key ? (activityAt[key] ?? 0) : 0;
      const at = Math.max(messageAt, liveAt);
      return { index, at: at || Number.POSITIVE_INFINITY };
    })
    .sort((a, b) => b.at - a.at || a.index - b.index)
    .map(entry => entry.index);
}
